import { IntakeSectionHeader, StyledGridRow } from "@/common/styles";
import { staticFields } from "@/util/common_variable";
import { Formik } from "formik";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { FormContainer } from "./styles";
import DynamicForm from "@/common/DynamicForm";
import { Form } from "antd";
import dayjs from "dayjs";
import {
  findAgefromDOB,
  getVisibleFields,
  hasCheckSecurity,
  isNonEmptyVariable,
} from "@/util/common_function";
import { useDispatch, useSelector } from "react-redux";
import { setBookings } from "@/slices/bookingsSlice";
import FieldVerificationContext, {
  useFieldVerificationState,
} from "@/contexts/FieldVerificationContext";
import VerificationHint from "@/common/VerificationHint";
import { useClientParamBool } from "@/hooks/useClientParam";

export const PERSON = {
  INMATE: "INMATE",
  VISITOR: "VISITOR",
  VISITOR_LOG: "VISITOR_LOG",
  BOOKINGS: "BOOKINGS",
  LOW: "LOW",
  STAFF: "STAFF",
  BAILPAYMENT: "BAILPAYMENT",  
};

const Personal = forwardRef(
  ({
    header,
    person = PERSON.INMATE,
    personalFormRef,
    formData = {},
    onUpdateForm = () => {},
    startingPoint = "",
    highlightFields = [],
  }, ref) => {
    const dispatch = useDispatch();
    const { bookings, bookingsFieldsEnabled } = useSelector(
      (state) => state?.bookings
    );
    const [form] = Form.useForm();
    const [personalInfoForm, setPersonalInfoForm] = useState(null);
    const fullSearch = useSelector(state => state.fullSearch);
    // client-param-driven Bio field visibility (mirrors ASP's BioInterface.asp)
    const showQuestionedIdentity = useClientParamBool("Bio", "DisplayQuestionIdentity", false);
    const multipleAliasesEnabled = useClientParamBool("Bio", "MultipleAliases", false);
    const visiblePersonalInfoFields = (staticFields?.personalInfo[person] || []).filter((sf) => {
      if (sf.columnName === "questionedIdentity") return showQuestionedIdentity;
      if (sf.hideWhenMultipleAliases) return !multipleAliasesEnabled;
      return true;
    });
    // Verification applies to any flow that links an existing jacket/person
    // onto this form — Bookings intake and Warrants (both default to
    // PERSON.BOOKINGS/PERSON.INMATE here) — but not Staff/Visitor uses of
    // this same component, which pass their own distinct PERSON.* value and
    // keep today's behavior.
    const isBookingsFlow = person === PERSON.INMATE || person === PERSON.BOOKINGS;
    // Warrants gets its own RequireVerificationOfJacketFields client-param
    // context (ParamContext="Warrants") instead of piggy-backing on Bookings',
    // matching the per-module config pattern used elsewhere in this app.
    const verification = useFieldVerificationState(
      startingPoint === "Warrants" ? "Warrants" : "Bookings"
    );
    // FolderSearch.openSelected sets sessionStorage["from-jacket"]="true" right
    // before navigating here with an existing person's bio already in Redux —
    // this ref makes sure we only react to that once per mount, not on every
    // subsequent bookings-state change (e.g. the user editing other tabs).
    const hasHandledFreshJacketRef = useRef(false);

    useEffect(() => {
      const bio = bookings?.bio?.[0];
      // const visitor = formData || {};
      // if (person === PERSON.VISITOR && isNonEmptyVariable(visitor)) {
      //   setPersonalInfoForm(() => ({
      //     ...visitor,
      //     dob: dayjs(),
      //   }));
      // } else
      if (isNonEmptyVariable(bio)) {
        setPersonalInfoForm(() => ({
          ...bio,
          complexion: bio?.complexion?.trim() ?? null,
          glasses: bio?.glasses?.trim() ?? null,
          dob: isNonEmptyVariable(bio?.dob) ? dayjs(bio?.dob) : null,
          dlExpiration: isNonEmptyVariable(bio?.dlExpiration)
            ? dayjs(bio?.dlExpiration)
            : null,
          ageAtEntry: isNonEmptyVariable(bio?.dob)
            ? findAgefromDOB(dayjs(bio?.dob)?.format("MM/DD/YYYY"))
            : null,
        }));

        if (
          isBookingsFlow &&
          !hasHandledFreshJacketRef.current &&
          sessionStorage.getItem("from-jacket") === "true" &&
          // FolderSearch's "Create New Folder" also sets from-jacket=true
          // (it needs to, to make the landing page show its booking form —
          // see FolderSearch.createNew) even though no existing jacket was
          // linked. jacket-new-folder=true is how it tells us this bio data
          // came from typed search values on a brand-new folder, not from
          // an existing jacket that needs verification.
          sessionStorage.getItem("jacket-new-folder") !== "true"
        ) {
          hasHandledFreshJacketRef.current = true;
          // Skip disabled/computed fields (e.g. ageAtEntry) — the user has no
          // way to clear those, so marking them unverified would permanently
          // block save, same reason ASP excludes disabled fields from this.
          const copiedFields = (staticFields?.personalInfo?.[person] || [])
            .filter((sf) => !sf.disabled)
            .map((sf) =>
              Array.isArray(sf.columnName)
                ? sf.columnName[sf.columnName.length - 1]
                : sf.columnName
            )
            .filter((colName) => bio?.[colName]);
          if (copiedFields.length) verification.markUnverified(copiedFields);
        }
      } else {
        setPersonalInfoForm(null);
      }
      // setDlNumberEntered(!!bio?.dlNumber?.trim());
    }, [bookings]);

    useEffect(() => {
      if (personalInfoForm === null) {
        form.resetFields();
      } else {
        form.setFieldsValue(personalInfoForm);
      }
    }, [personalInfoForm]);

    // Expose form methods to parent
    useImperativeHandle(personalFormRef, () => ({
      submitForm: () => form.submit(),
      resetForm: () => form.resetFields(),
      getFormValues: () => form.getFieldsValue(),
      getFieldsError: () => form.getFieldsError(),
      isFieldsValidating: () => form.isFieldsValidating(),
      validateFields: (options) => form.validateFields(options),
      form: form,
      hasUnverifiedFields: verification.hasUnverifiedFields,
      getUnverifiedFieldNames: verification.getUnverifiedFieldNames,
    }));

    const updateForm = (data) => {
      if (person === PERSON.VISITOR) {
        onUpdateForm(data);
      } else {
        dispatch(
          setBookings({
            bio: [
              {
                ...bookings?.bio?.[0],
                ...data,
              },
            ],
            arrestReports: [
              {
                ...bookings?.arrestReports?.[0],
                inmateTypeId: !fullSearch ? (data?.ageAtEntry < 18 ? 154540005 : 154540001) : null,
              },
            ],
          })
        );
      }
    };
    // console.log(personalInfoForm)

    
    return (
      <FormContainer>
        {header && (
          <IntakeSectionHeader>
            <span>{header}</span>
          </IntakeSectionHeader>
        )}

        {/* Safe to always provide: markUnverified is only ever called above when
            isBookingsFlow is true, so for Staff/Visitor uses of this component
            the unverified set stays empty and nothing changes. */}
        <FieldVerificationContext.Provider value={verification}>
        <VerificationHint />
        <Form
          form={form}
          disabled={!bookingsFieldsEnabled}
          initialValues={personalInfoForm}
          onBlur={(e) => {
            const ignoredClasses = [
              "ant-picker-header-super-next-btn",
              "ant-picker-date-panel",
              "ant-picker-header-super-prev-btn",
            ];
            const ignoredIds = ["dob", "dlExpiration"];
            const isIgnored =
              ignoredClasses.includes(e.target.className) ||
              ignoredIds.includes(e.target.id);

            if (isIgnored) return;

            const values = form.getFieldsValue();
            values.dob = values?.dob
              ? dayjs(values?.dob)?.format("MM/DD/YYYY")
              : null;
            values.dlExpiration = values?.dlExpiration
              ? dayjs(values?.dlExpiration)?.format("MM/DD/YYYY")
              : null;
            updateForm(values);
          }}
          onFieldsChange={(sf) => {
            // if (sf?.[0]?.name?.[0] === "dlNumber") {
            //   const val = sf[0].value;
            //   setDlNumberEntered(!!val?.trim());
            //   // Re-validate dlExpiration whenever dlNumber changes
            //   form.validateFields(["dlExpiration"]);
            // }
            if (
              sf?.[0]?.name?.[0] === "dob" ||
              sf?.[0]?.name?.[0] === "dlExpiration"
            ) {
              const values = form.getFieldsValue();

              const data = {
                dob: values?.dob
                  ? dayjs(values?.dob)?.format("MM/DD/YYYY")
                  : null,
                dlExpiration: values?.dlExpiration
                  ? dayjs(values?.dlExpiration)?.format("MM/DD/YYYY")
                  : null,
              };

              const age = findAgefromDOB(data?.dob);

              data.ageAtEntry = age;
              updateForm(data);
            }
          }}
        >
          <StyledGridRow gutter={[16]}>
            {visiblePersonalInfoFields.map((sf, index) => {

              let view = hasCheckSecurity(
                "Functionality",
                "field",
                "VIEW",
                "",
                "",
                sf.tableName,
                sf.columnName,
                startingPoint
              );
              let edit = hasCheckSecurity(
                "Functionality",
                "field",
                "MODIFY",
                "",
                "",
                sf.tableName,
                sf.columnName,
                startingPoint
              );
              return (
                <DynamicForm
                  formAttributes={sf}
                  i={index}
                  form={form}
                  bookingsFieldsEnabled={bookingsFieldsEnabled}
                  view={view}
                  edit={edit}
                  person={person}
                  fullSearch={fullSearch}
                  highlight={highlightFields.includes(sf.columnName)}
                  startingPoint={startingPoint}
                />
              );
            })}
          </StyledGridRow>
        </Form>
        </FieldVerificationContext.Provider>
      </FormContainer>
    );
  }
);

export default Personal;
